<template>
  <div v-if="listState.bulkActions.enabled || isBulkAdd" class="oeb-BulkCheckbox">
    <AppCheckbox
      v-if="isBulkAdd"
      v-model="bulkAddState.checkedElements"
      v-tippy="{
        content: bulkAddState.getDisabledState(okrElement).tooltip,
        ...tooltipConfig
      }"
      :disabled="bulkAddState.getDisabledState(okrElement).disabled"
      :value="okrElement[OKR_ELEMENT_ENTITY_KEYS.ID]"
      data-testid="bulk-add-checkbox"
      full-height
      full-width
      name="otr-bulk-add-checkbox"
    />

    <AppCheckbox
      v-else
      v-model="listState.bulkActions.selectedElements"
      v-tippy="{
        content: bulkCheckboxDisableState.tooltip,
        ...tooltipConfig
      }"
      :disabled="bulkCheckboxDisableState.disabled"
      :value="okrElement[OKR_ELEMENT_ENTITY_KEYS.ID]"
      data-testid="bulk-checkbox"
      full-height
      full-width
      name="otr-bulk-actions-checkbox"
    />
  </div>
</template>

<script setup>
import { isNull } from 'lodash'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'

import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { OKR_ELEMENT_ENTITY_KEYS } from '@/utils/entity-keys'
import {
  bulkAddStateInjectionKey,
  isAzureAppInjectionKey,
  listStateInjectionKey
} from '@/utils/injection-keys'
import { currentUserCanUpdateObjective, isOkrElementClosed } from '@/utils/objectives'
import { MAX_SELECTED_ELEMENTS_FOR_BULK_ACTIONS } from '@/utils/okr-elements-table-bulk-actions'

import AppCheckbox from '@/components/ui/AppCheckbox/AppCheckbox'

defineOptions({
  name: 'OkrElementBulkCheckbox'
})

const props = defineProps({
  okrElement: {
    type: Object,
    required: true
  }
})

const bulkAddState = inject(bulkAddStateInjectionKey, null)
const listState = inject(listStateInjectionKey)

const isBulkAdd = computed(() => !isNull(bulkAddState))

const { t } = useI18n()

const isAzureApp = inject(isAzureAppInjectionKey)

const bulkCheckboxDisableState = computed(() => {
  const hasUpdateAccess = currentUserCanUpdateObjective(props.okrElement)

  const state = {
    disabled: false,
    tooltip: null
  }

  if (props.okrElement.isNestedTask) {
    state.disabled = true
    state.tooltip = t('bulk_actions.disabled_for_nested_issue')

    if (isAzureApp) {
      state.tooltip = t('bulk_actions.disabled_for_nested_work_item')
    }

    return state
  }

  if (!hasUpdateAccess) {
    state.disabled = true
    state.tooltip = t('bulk_actions.disabled_by_permissions')
    return state
  }

  if (isOkrElementClosed(props.okrElement)) {
    state.disabled = true
    state.tooltip = t('bulk_actions.disabled_by_status', {
      closedStatus: t('dashboard.closed'),
      abandonedStatus: t('dashboard.abandoned')
    })
    return state
  }

  const isLimitExceeded =
    listState.bulkActions.selectedElements.length >= MAX_SELECTED_ELEMENTS_FOR_BULK_ACTIONS

  if (isLimitExceeded) {
    state.disabled = !listState.bulkActions.selectedElements.includes(
      props.okrElement[OKR_ELEMENT_ENTITY_KEYS.ID]
    )
    state.tooltip = state.disabled
      ? t('bulk_actions.disabled_by_count', MAX_SELECTED_ELEMENTS_FOR_BULK_ACTIONS)
      : null

    return state
  }

  return state
})

const tooltipConfig = computed(() => {
  return {
    theme: `${DROP_LIST_THEMES.COMMON_TOOLTIP_THEMES} ${DROP_LIST_THEMES.TEXT_CENTER}`,
    placement: 'top',
    arrow: true,
    offset: [0, -2],
    maxWidth: 296
  }
})
</script>

<style lang="scss" scoped>
.oeb-BulkCheckbox {
  width: var(--okr-table-row-controls-button-width);
  position: absolute;
  left: var(--page-left-padding);
  height: 100%;
  --label-justify-content: center;
}
</style>
